/**
 * Confirmation for deleting a forge (ADR-023 A8).
 *
 * States what goes and what stays, because the two are easy to confuse: the
 * workspace goes for good, and a published arche is unpublished — but the
 * arche itself (its history, submissions and earnings) is kept.
 *
 * The user types "delete" to confirm, as in the Arche Builder: the workspace
 * cannot be recovered. The dialog stays open while the delete runs and shows a
 * failure inline, so the user never has to wonder whether the click took.
 */
import { useState } from 'react';
import { Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { ForgeSummary } from '../types';
import { forgeDisplayName } from './ForgeNameEditor';

/** What the user types to confirm. */
export const FORGE_DELETE_CONFIRM_WORD = 'delete';

const RUNTIME_STATUSES: ReadonlySet<ForgeSummary['status']> = new Set([
  'provisioning',
  'running',
  'stopping',
]);

export interface ForgeDeleteDialogProps {
  /** The forge to delete; null closes the dialog. */
  forge: Pick<ForgeSummary, 'id' | 'archeId' | 'name' | 'status' | 'origin'> | null;
  onCancel: () => void;
  /** Resolves when the forge is deleted; a rejection is shown in the dialog. */
  onConfirm: (forgeId: string) => Promise<void>;
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'The forge could not be deleted. Try again.';
}

export function ForgeDeleteDialog({ forge, onCancel, onConfirm }: ForgeDeleteDialogProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typed, setTyped] = useState('');
  const confirmed = typed.trim().toLowerCase() === FORGE_DELETE_CONFIRM_WORD;

  const close = () => {
    if (isDeleting) return;
    setError(null);
    setTyped('');
    onCancel();
  };

  const confirm = async () => {
    if (!forge || !confirmed) return;
    setIsDeleting(true);
    setError(null);
    try {
      await onConfirm(forge.id);
      setTyped('');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setIsDeleting(false);
    }
  };

  const hasRuntime = forge ? RUNTIME_STATUSES.has(forge.status) : false;

  return (
    <AlertDialog open={forge !== null} onOpenChange={(open) => !open && close()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this forge?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>
                The workspace for{' '}
                <span className="font-medium">{forge ? forgeDisplayName(forge) : ''}</span> and all
                its files are deleted. This can&apos;t be undone.
              </p>
              <ul className="list-disc pl-5 space-y-1">
                {hasRuntime && (
                  <li>The forge is running. It stops, and any turn in progress ends.</li>
                )}
                <li>
                  If the arche is published, it is unpublished: it leaves the Arche Store and no
                  longer opens for anyone. The arche itself and its history are kept.
                </li>
                <li>Its test builds stop being available.</li>
                {forge?.origin === 'builder' && (
                  <li>The Arche Builder session it came from stays read-only.</li>
                )}
              </ul>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2">
          <Label htmlFor="forge-delete-confirm" className="text-sm font-normal">
            Type <span className="font-mono font-semibold">{FORGE_DELETE_CONFIRM_WORD}</span> to
            confirm
          </Label>
          <Input
            id="forge-delete-confirm"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && confirmed && !isDeleting) void confirm();
            }}
            placeholder={FORGE_DELETE_CONFIRM_WORD}
            autoComplete="off"
            disabled={isDeleting}
          />
        </div>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={() => void confirm()}
            disabled={isDeleting || !confirmed}
          >
            {isDeleting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Trash2 className="h-4 w-4 mr-2" />
            )}
            Delete forge
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
